// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Link from 'components/common/Link';
import { Popover, Icon, Hint, Status } from '@plesk/ui-library';
import { messageBox, redirect, api, Locale } from 'jsw';

const Translate = Locale.getTranslate('components.view-switcher');

const ViewSwitcher = ({
    isPowerUserPanel,
    showPowerUserViewWarning,
    learnMoreUrl,
    hideSwitcherUrl,
    changeViewUrl,
}) => {
    const [visible, setVisible] = useState(true);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const targetRef = useRef();

    const handleTogglePopover = () => {
        setPopoverVisible(!popoverVisible);
    };

    const handleHideSwitcher = () => {
        const locale = Locale.getSection('components.view-switcher');

        messageBox.show({
            type: messageBox.TYPE_YESNO,
            subtype: 'toggle',
            text: locale.lmsg('confirmationBoxText'),
            description: locale.lmsg('confirmationBoxDescription'),
            buttonTitles: {
                yes: locale.lmsg('confirmationBoxButtonYes'),
                no: locale.lmsg('confirmationBoxButtonNo'),
            },
            onYesClick: () => {
                setVisible(false);
                api.post(hideSwitcherUrl);
            },
        });
    };

    const handleChangeView = async e => {
        e.preventDefault();
        const { redirect: url } = await api.post(changeViewUrl);
        if (url) {
            redirect(url);
        }
    };

    const handleClosePopover = () => {
        setTimeout(() => {
            setPopoverVisible(false);
        }, 0);
    };

    const renderActionMessage = () => {
        const actionMessage = (
            <Translate
                content="actionMessage"
                params={{
                    switchLink: (
                        <Link
                            onClick={handleChangeView}
                            onTouchStart={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            data-action={isPowerUserPanel ? 'to-service-provider' : 'to-power-user'}
                        >
                            <Translate
                                content="switchView"
                                params={{ view: <Translate content={isPowerUserPanel ? 'serviceProviderView' : 'powerUserView'} /> }}
                            />
                        </Link>
                    ),
                    recommended: <Translate content={isPowerUserPanel ? 'serviceProviderViewRecommended' : 'powerUserViewRecommended'} />,
                    helpLink: (
                        <Link
                            data-action="learn-more"
                            to={learnMoreUrl}
                        >
                            <Translate content="learnMore" />
                        </Link>
                    ),
                }}
            />
        );

        return actionMessage;
    };

    const renderSwitcherPopover = target => (
        <Popover
            visible={popoverVisible}
            target={target}
            targetRef={targetRef}
            title={<Translate content="title" />}
            placement="top-right"
            onClose={handleClosePopover}
            canCloseOnOutsideClick
            canCloseOnEscapePress
            boundariesElement={document.querySelector('.pul-layout')}
            data-type="view-switcher"
            aria-live="polite"
        >
            <Translate
                content="currentViewMessage"
                params={{
                    view: <Translate content={isPowerUserPanel ? 'powerUserView' : 'serviceProviderView'} />,
                    hint: <Translate content={isPowerUserPanel ? 'powerUserViewHint' : 'serviceProviderViewHint'} />,
                }}
            />
            <p>
                {renderActionMessage()}
            </p>
            {showPowerUserViewWarning && (
                <Hint>
                    <Status intent="info">
                        <Translate content="powerUserViewWarning" />
                    </Status>
                </Hint>
            )}
        </Popover>
    );

    if (!visible) {
        return null;
    }

    const locale = Locale.getSection('components.view-switcher');

    return (
        <div className="view-switcher">
            {renderSwitcherPopover((
                <button
                    type="button"
                    className="view-switcher__button"
                    onClick={handleTogglePopover}
                    data-action="toggle-view-switcher"
                    ref={targetRef}
                    aria-label={locale.lmsg('title')}
                >
                    <Icon src={require('icons/16/plesk/nav-navigation.svg')} className="view-switcher__button-icon" />
                    <Translate content="title" className="view-switcher__button-text" component="span" />
                </button>
            ))}
            <button type="button" aria-label={locale.lmsg('hide')} className="view-switcher__close" onClick={handleHideSwitcher} data-action="hide-view-switcher" />
        </div>
    );
};

ViewSwitcher.propTypes = {
    isPowerUserPanel: PropTypes.bool.isRequired,
    showPowerUserViewWarning: PropTypes.bool.isRequired,
    learnMoreUrl: PropTypes.string.isRequired,
    hideSwitcherUrl: PropTypes.string.isRequired,
    changeViewUrl: PropTypes.string.isRequired,
};

export default ViewSwitcher;
